import type { ReactNode } from "react";
import { Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Composed home card — plan/50 widget catalog.
 * Sizes: compact (glance chips live inside OwnerGlanceBand) · list · wide.
 */
export function HomeWidget(props: {
  title: string;
  description?: string;
  size?: "list" | "wide";
  children: ReactNode;
  className?: string;
  actions?: ReactNode;
  /** Gear → Customize focused on this widget (plan/50 C). */
  onCustomize?: () => void;
}) {
  const size = props.size ?? "list";
  const gear = props.onCustomize ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-8 w-8 shrink-0 p-0 text-[var(--color-muted)]"
      aria-label={`Customize ${props.title}`}
      onClick={props.onCustomize}
    >
      <Settings2 className="h-4 w-4" />
    </Button>
  ) : null;

  return (
    <section
      className={cn(
        "rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-card)] p-4 shadow-[var(--shadow-soft)]",
        size === "wide" && "lg:col-span-full",
        props.className,
      )}
      data-home-widget={size}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="m-0 font-[family-name:var(--font-display)] text-base font-semibold tracking-tight text-[var(--color-foreground)]">
            {props.title}
          </h2>
          {props.description ? (
            <p className="mt-1 text-sm text-[var(--color-muted)]">
              {props.description}
            </p>
          ) : null}
        </div>
        {(props.actions || gear) && (
          <div className="flex shrink-0 items-center gap-1">
            {props.actions}
            {gear}
          </div>
        )}
      </div>
      <div className="mt-4">{props.children}</div>
    </section>
  );
}
